import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Button } from 'applaus-ui-kit'
import { useFormik } from 'formik'
import {
  acceptTicketTransferService,
  getTicketTransferByTokenService,
  getTransferPasswordErrorMessage,
  rejectTicketTransferService,
  TransferDetailsResponse
} from 'api/services/ticketTransfer/ticketTransferService'
import { passwordSchema } from 'components/TransferTicketModal/validationSchema'
import * as S from './TicketTransfer.styles'
import moment from 'moment'
import { ReduxState } from 'redux/rootReducer'

export const TicketTransferPage = () => {
  const { token } = useParams<{ token: string }>()
  const {
    accessToken,
    isAuthenticated,
    loading: authLoading
  } = useSelector((state: ReduxState) => state.UserReducer)
  const [details, setDetails] = useState<TransferDetailsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const passwordForm = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      if (!details) return
      setSubmitting(true)
      setErrorMessage('')
      try {
        await acceptTicketTransferService(details.id, values.password)
        setSuccessMessage(
          'Transferência aceita com sucesso. O ingresso já está na sua conta.'
        )
        setShowPasswordModal(false)
      } catch (error: any) {
        setErrorMessage(getTransferPasswordErrorMessage(error))
        passwordForm.setFieldValue('password', '')
        passwordForm.setFieldValue('confirmPassword', '')
      } finally {
        setSubmitting(false)
      }
    }
  })

  useEffect(() => {
    const load = async () => {
      if (!token) return
      if (authLoading || !accessToken || !isAuthenticated) {
        return
      }
      setLoading(true)
      setErrorMessage('')
      try {
        const data = await getTicketTransferByTokenService(token)
        setDetails(data)
      } catch (error: any) {
        setErrorMessage(
          error?.response?.data?.message?.message ||
            error?.response?.data?.message ||
            'Não foi possível carregar a solicitação de transferência.'
        )
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [token, accessToken, isAuthenticated, authLoading])

  const handleReject = async () => {
    if (!details) return
    setSubmitting(true)
    setErrorMessage('')
    try {
      await rejectTicketTransferService(details.id)
      setSuccessMessage(
        'Transferência recusada. O ingresso voltou para o remetente.'
      )
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message?.message ||
          error?.response?.data?.message ||
          'Não foi possível recusar a transferência.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <S.Page>
        <S.Card>
          <p>Carregando solicitação...</p>
        </S.Card>
      </S.Page>
    )
  }

  if (!details) {
    return (
      <S.Page>
        <S.Card>
          <h1>Transferência de ingresso</h1>
          <S.ErrorText>
            {errorMessage || 'Solicitação não encontrada.'}
          </S.ErrorText>
        </S.Card>
      </S.Page>
    )
  }

  const senderName =
    `${details.sender.firstName} ${details.sender.lastName}`.trim()

  return (
    <S.Page>
      <S.Card>
        <h1>Solicitação de transferência</h1>
        <S.InfoRow>
          <strong>Remetente</strong>
          <span>{senderName}</span>
        </S.InfoRow>
        <S.InfoRow>
          <strong>Evento</strong>
          <span>{details.event.name}</span>
        </S.InfoRow>
        <S.InfoRow>
          <strong>Data</strong>
          <span>{details.event.date}</span>
        </S.InfoRow>
        <S.InfoRow>
          <strong>Local</strong>
          <span>{details.event.location}</span>
        </S.InfoRow>
        <S.InfoRow>
          <strong>Solicitado em</strong>
          <span>{moment(details.createdAt).format('DD/MM/YYYY HH:mm')}</span>
        </S.InfoRow>
        {details.event.url ? (
          <S.EventLink
            href={details.event.url}
            target="_blank"
            rel="noreferrer">
            Ver página do evento
          </S.EventLink>
        ) : null}

        {errorMessage ? <S.ErrorText>{errorMessage}</S.ErrorText> : null}
        {successMessage ? (
          <S.SuccessText>{successMessage}</S.SuccessText>
        ) : null}

        {!successMessage ? (
          <S.Actions>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              color="primary"
              disabled={submitting}
              onClick={handleReject}>
              Recusar
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="small"
              color="primary"
              disabled={submitting}
              onClick={() => {
                setErrorMessage('')
                setShowPasswordModal(true)
              }}>
              Aceitar
            </Button>
          </S.Actions>
        ) : null}
      </S.Card>

      {showPasswordModal ? (
        <S.ModalOverlay>
          <S.ModalContent>
            <p>Confirme sua identidade para aceitar o ingresso</p>
            <S.Input
              name="password"
              type="password"
              placeholder="Digite sua senha"
              value={passwordForm.values.password}
              onChange={passwordForm.handleChange}
              onBlur={passwordForm.handleBlur}
            />
            <S.Input
              name="confirmPassword"
              type="password"
              placeholder="Confirme sua senha"
              value={passwordForm.values.confirmPassword}
              onChange={passwordForm.handleChange}
              onBlur={passwordForm.handleBlur}
            />
            {errorMessage ? <S.ErrorText>{errorMessage}</S.ErrorText> : null}
            <S.Actions>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => setShowPasswordModal(false)}>
                Cancelar
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="primary"
                disabled={submitting}
                onClick={() => passwordForm.handleSubmit()}>
                Confirmar
              </Button>
            </S.Actions>
          </S.ModalContent>
        </S.ModalOverlay>
      ) : null}
    </S.Page>
  )
}
